// What the user reads about a failed execution of an Efficiency Test: the text, the kind of icon and the
// command, from one failure of `failures[]` or from a failed operation (`GET operations/{id}`).
//
// Pure: no component builds a string of its own. The principle (backend plan, §0): the user is always told
// that the execution did not complete; the advice comes only with what failed, for which certificate, and
// an action that exists on the screen. Whatever is not known - a reason, an action, a certificate that
// should be named - falls to the general text of the part where it happened, with no advice.
//
// The backend gives the reason as a code and the action as a constant; the words are in the locale files
// under `efficiencyTestFailures`. The failure's own `id` and the `operationId` are never shown.

export const FAILURE_ACTIONS = {
  EXCLUDE_CERTIFICATE: 'ExcludeCertificate',
  UPDATE_COMPARABLES: 'UpdateComparables',
  RETRY: 'Retry',
  NONE: 'None',
}

// What the user sees: an icon, red for a problem that needs a change, amber for one that may not come back.
export const FAILURE_TYPES = {
  BLOCKING: 'blocking',
  TRANSIENT: 'transient',
}

// The family of the text: with an action to take, with a new attempt to make, or general (no advice).
export const FAILURE_FAMILIES = {
  ACTION: 'action',
  RETRY: 'retry',
  GENERAL: 'general',
}

const KNOWN_ACTIONS = Object.values(FAILURE_ACTIONS)

const A = FAILURE_ACTIONS

// The reasons that have a text of their own: the action they go with in the execution of a test, and whether
// the text names a certificate (`{isin}`). A reason with another action, or a certificate-bound one that
// arrives with no certificate, has no honest text of its own and is told in general terms.
export const REASON_TEXTS = {
  SecondaryIsinUnknown: { action: A.EXCLUDE_CERTIFICATE, certificate: true },
  SecondaryPdfNotFound: { action: A.EXCLUDE_CERTIFICATE, certificate: true },
  SecondaryPdfInvalid: { action: A.EXCLUDE_CERTIFICATE, certificate: true },
  ExtractionRefused: { action: A.EXCLUDE_CERTIFICATE, certificate: true },
  ImportRejected: { action: A.EXCLUDE_CERTIFICATE, certificate: true },
  IssuerNotRegistered: { action: A.EXCLUDE_CERTIFICATE, certificate: true },
  ComparableNotResolved: { action: A.UPDATE_COMPARABLES, certificate: true },
  SecondaryMarketUnreachable: { action: A.RETRY, certificate: true },
  SecondaryPdfLookupFailed: { action: A.RETRY, certificate: true },
  SecondaryPdfDownloadFailed: { action: A.RETRY, certificate: true },
  ExtractionFailed: { action: A.RETRY, certificate: true },
  ImportedCertificateNotFound: { action: A.RETRY, certificate: true },
  CalibrationFailed: { action: A.RETRY, certificate: false },
  ScoringFailed: { action: A.RETRY, certificate: false },
  SnapshotFailed: { action: A.RETRY, certificate: false },
  ResultNotSaved: { action: A.RETRY, certificate: false },
  ScoresNotSaved: { action: A.RETRY, certificate: false },
  HostStopped: { action: A.RETRY, certificate: false },
  ExecutionReleased: { action: A.RETRY, certificate: false },
}

const PARTS = { IMPORT: 'import', COMPUTATION: 'computation', UNKNOWN: 'unknown' }

// The part of the execution, from the `stage` of a failure.
const PART_OF_STAGE = {
  PD: PARTS.IMPORT,
  Dsh: PARTS.IMPORT,
  Doxa: PARTS.IMPORT,
  Import: PARTS.IMPORT,
  Computation: PARTS.COMPUTATION,
}

// The part of the execution, from the `step` of an operation. Checking the comparables and saving are not
// told on their own: the general text has no sentence for them.
const PART_OF_STEP = {
  FetchingSecondary: PARTS.IMPORT,
  WaitingResources: PARTS.IMPORT,
  Scoring: PARTS.COMPUTATION,
}

// Operation codes that still have a text: they say what happened. The generic blocking and transient codes
// do not: nothing in the text derives from them any more.
const OPERATION_TEXTS = {
  ExecutionInterrupted: 'efficiencyTestsList.executionNotices.failed.ExecutionInterrupted',
  OperationLost: 'efficiencyTestsList.executionNotices.failed.OperationLost',
}

const BASE = 'efficiencyTestFailures.'

function generalText(part) {
  return BASE + 'general.' + part
}

function normalizedAction(action) {
  return KNOWN_ACTIONS.includes(action) ? action : A.NONE
}

function certificateOf(failure) {
  return failure.isin || failure.productName || null
}

function presentFailure(failure, options) {
  const type = failure.isBlocking ? FAILURE_TYPES.BLOCKING : FAILURE_TYPES.TRANSIENT
  const command = normalizedAction(failure.action)
  const entry = REASON_TEXTS[failure.reason]
  const certificate = certificateOf(failure)

  const hasOwnText = entry && entry.action === failure.action && (!entry.certificate || certificate)
  if (!hasOwnText) {
    const part = PART_OF_STAGE[failure.stage] || PARTS.UNKNOWN
    return {
      type,
      family: FAILURE_FAMILIES.GENERAL,
      textKey: generalText(part),
      textParams: {},
      command,
    }
  }

  // A comparable added by hand is taken out, not excluded: same advice, the verb of the control that exists.
  const verb = entry.action === A.EXCLUDE_CERTIFICATE && options.manuallyAdded ? 'removeReasons.' : 'reasons.'
  return {
    type,
    family: entry.action === A.RETRY ? FAILURE_FAMILIES.RETRY : FAILURE_FAMILIES.ACTION,
    textKey: BASE + verb + failure.reason,
    textParams: { isin: certificate },
    command,
  }
}

// The commands of a failed certificate import (`errorAction` of the operation, `OperationActions` in the backend).
export const IMPORT_ACTIONS = {
  UPLOAD_ANOTHER: 'UploadAnother',
  RETRY: 'Retry',
  USE_MARKET_PRODUCT: 'UseMarketProduct',
  NONE: 'None',
}

const CERTIFICATE_IMPORT_KIND = 'CertificateImport'
const IMPORT_BASE = 'certificateImportFailures.'
const KNOWN_IMPORT_ACTIONS = Object.values(IMPORT_ACTIONS)

// The reason of an import from the `errorCode` of its operation (CertificateImportFailureCodes in the backend),
// with the action its text goes with and whether it needs a change (blocking) or may not come back. A code not
// listed (`CertificateImportFailed` included: an unexpected failure has no advice) is told in general terms.
const IMPORT_REASONS = {
  Unprocessable: { reason: 'ExtractionRefused', action: IMPORT_ACTIONS.UPLOAD_ANOTHER, blocking: true },
  CertificateExtractionFailed: { reason: 'ExtractionFailed', action: IMPORT_ACTIONS.RETRY, blocking: false },
  CertificateImportRejected: { reason: 'ImportRejected', action: IMPORT_ACTIONS.UPLOAD_ANOTHER, blocking: true },
  MarketProductWithSameNameExists: {
    reason: 'MarketProductSameName',
    action: IMPORT_ACTIONS.USE_MARKET_PRODUCT,
    blocking: true,
  },
  OperationLost: { reason: 'HostStopped', action: IMPORT_ACTIONS.UPLOAD_ANOTHER, blocking: false },
}

// The part of the import, from the `step` of the operation.
const IMPORT_PART_OF_STEP = { Extracting: 'read', Importing: 'import' }

function presentImportOperation(operation) {
  const entry = IMPORT_REASONS[operation.errorCode]
  const action = KNOWN_IMPORT_ACTIONS.includes(operation.errorAction) ? operation.errorAction : IMPORT_ACTIONS.NONE

  if (entry && entry.action === action) {
    return {
      type: entry.blocking ? FAILURE_TYPES.BLOCKING : FAILURE_TYPES.TRANSIENT,
      family: action === IMPORT_ACTIONS.RETRY ? FAILURE_FAMILIES.RETRY : FAILURE_FAMILIES.ACTION,
      textKey: IMPORT_BASE + 'reasons.' + entry.reason,
      textParams: {},
      command: action,
    }
  }

  return {
    type: FAILURE_TYPES.TRANSIENT,
    family: FAILURE_FAMILIES.GENERAL,
    textKey: IMPORT_BASE + 'general.' + (IMPORT_PART_OF_STEP[operation.step] || 'unknown'),
    textParams: {},
    command: action,
  }
}

function presentOperation(operation) {
  if (operation.kind === CERTIFICATE_IMPORT_KIND) {
    return presentImportOperation(operation)
  }

  const textKey = OPERATION_TEXTS[operation.errorCode]
  const type = operation.errorCode === 'ExecutionFailedBlocking' ? FAILURE_TYPES.BLOCKING : FAILURE_TYPES.TRANSIENT
  if (textKey) {
    return {
      type,
      family: FAILURE_FAMILIES.RETRY,
      textKey,
      textParams: {},
      command: A.RETRY,
    }
  }

  return {
    type,
    family: FAILURE_FAMILIES.GENERAL,
    textKey: generalText(PART_OF_STEP[operation.step] || PARTS.UNKNOWN),
    textParams: {},
    command: normalizedAction(operation.errorAction),
  }
}

/**
 * @param {object} source one element of `failures[]` ({ reason, isBlocking, stage, action, isin, productName })
 *   or a failed operation ({ kind, errorCode, errorAction, step }); a `CertificateImport` one has its own texts
 *   and commands. Nothing, or nothing known, is the general text.
 * @param {{ manuallyAdded?: boolean }} [options] `manuallyAdded`: the row is a comparable added by hand.
 * @returns {{ type: string, family: string, textKey: string, textParams: object, command: string }}
 *   `type` for the icon, `family` for the order and the wording, `textKey`/`textParams` for `$t`, `command`
 *   one of FAILURE_ACTIONS.
 */
export function failurePresentation(source, options = {}) {
  if (!source) {
    return presentFailure({}, options)
  }
  // An operation carries `errorCode`; a failure carries `reason`.
  if ('errorCode' in source && !('reason' in source)) {
    return presentOperation(source)
  }
  return presentFailure(source, options)
}
